import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  UntypedFormArray
} from '@angular/forms';
import { ProfileService } from 'core/services/profile.service';
import { QuestionBase } from '../../components/forms/question-base';
import { UtilsService } from 'core/services/utils.service';

@Injectable()
export class QuestionControlService {
  constructor(public fb: UntypedFormBuilder, public profileService: ProfileService) {}

  toFormGroup(questions: QuestionBase<any>[], action?: string) {
    return new UntypedFormGroup({});
  }

  public createControls(questions: QuestionBase<any>[] = [], parent: AbstractControl, action?: string) {
    questions.forEach((question) => {
      this.createControl(question, parent, action);
    });
  }

  isDefined(value: any) {
    // test null and undefined
    return value != null;
  }

  createControl(question: QuestionBase<any>, parent: AbstractControl, action?: string) {
    if (question) {
      question.action = action;
      if (question.key) {
        if (question.controlType === 'group') {
          const newGroup = this.fb.group({});
          this.addChildControl(question, newGroup, parent);
          this.createControls(question.childQuestions, newGroup, action);
        } else {
          const control = this.fb.control(
            this.isDefined(question.value) ? question.value : '',
            this.getValidators(question)
          );
          if (question.disabled || !this.profileService.canExecute(action)) {
            control.disable();
          } else {
            control.enable();
          }
          this.addChildControl(question, control, parent);
          if (question.childQuestions) {
            this.createControls(question.childQuestions, parent, action);
          }
        }
      } else {
        this.createControls(question.childQuestions, parent, action);
      }
    }
  }

  addChildControl(childQuestion: QuestionBase<any>, child: AbstractControl, parent: AbstractControl) {
    childQuestion.control = child;
    if (parent instanceof UntypedFormGroup) {
      parent.setControl(childQuestion.key, child);
    } else if (parent instanceof UntypedFormArray) {
      parent.push(child);
    }
  }

  getValidators(question: any) {
    const validators: any[] = [];
    if (question.validators) {
      if (question.validators.required) {
        validators.push(Validators.required);
      }
      if (question.validators.number) {
        validators.push(Validators.pattern(/^[0-9]*$/));
      }
      if (question.validators.mail) {
        validators.push(Validators.pattern(UtilsService.EMAIL_REGEXP));
      }
      if (question.validators.videoMediaUrl) {
        // Allow urls from 'youtube' and 'vimeo'
        validators.push(Validators.pattern(UtilsService.VIDEO_MEDIA_REGEXP));
      }
      if (question.validators.maxLength) {
        validators.push(Validators.maxLength(question.validators.maxLength));
      }
      if (question.validators.minLength) {
        validators.push(Validators.minLength(question.validators.minLength));
      }
      if (question.validators.invalidDate) {
        validators.push((formControl: UntypedFormControl) => {
          const value = formControl.value;
          return value && value instanceof Date && isNaN(value.getTime()) ? { invalidDate: true } : null;
        });
      }
      if (question.validators.minDate) {
        // TODO The check is done in the date component (question-datepicker), but it should also be checked here
      }
      if (question.validators.maxDate) {
        // TODO The check is done in the date component (question-datepicker), but it should also be checked here
      }
      if (question.validators.maxValue) {
        validators.push((formControl: UntypedFormControl) =>
          formControl.value > question.validators.maxValue.limit
            ? { maxValue: { limit: question.validators.maxValue.limit } }
            : null
        );
      }
      if (question.validators.minValue) {
        validators.push((formControl: UntypedFormControl) =>
          formControl.value < question.validators.minValue.limit
            ? { minValue: { limit: question.validators.minValue.limit } }
            : null
        );
      }
      if (question.validators.playTime) {
        validators.push((formControl: UntypedFormControl) => {
          const limit = question.validators.playTime.max;
          if (formControl.value > limit) {
            return { maxPlayTime: { limit } };
          } else if (formControl.value < 1) {
            return { minPlayTime: true };
          }
          return null;
        });
      }
      if (question.validators.function) {
        validators.push((formControl: UntypedFormControl) => question.validators.function(formControl, question));
      }
      if (question.validators.regexp) {
        validators.push(Validators.pattern(question.validators.regexp));
      }
    }

    return Validators.compose(validators);
  }
}
